import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { SUPPORT_DESK_CHANNELS, SUPPORT_TICKET_CATEGORIES, type SupportDeskChannel, type SupportTicketCategory } from '@vello/api-client';
import { Button, Field, Notice, Select, Row, FormStack } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { FormDialog } from './form-dialog';

const CHANNEL_ID = 'ride-ticket-channel';
const CATEGORY_ID = 'ride-ticket-category';

export interface RideTicketDialogProps {
  readonly open: boolean;
  readonly rideCode: string;
  /** The category the draft starts on (a cash ride not paid in full is a PAYMENT matter). */
  readonly defaultCategory: SupportTicketCategory;
  readonly busy: boolean;
  readonly error: string | undefined;
  readonly onCancel: () => void;
  readonly onConfirm: (input: { readonly channel: SupportDeskChannel; readonly category: SupportTicketCategory }) => void;
}

/**
 * "Abrir chamado" from the ride page (W8-FINAL-FARE-AND-CASH, D438; finding A-04). The ride IS the subject: the ticket is
 * opened through the support desk's own `POST /v1/support/tickets/for-caller` with `subject: { kind: 'RIDE', id }` in the
 * ride's market (no lookup — the page already shows the ride), so the agent only picks how the contact came in and what
 * it is about. The labels are the desk's own (`desk.channel.*`, `desk.category.*`).
 */
export function RideTicketDialog({ open, rideCode, defaultCategory, busy, error, onCancel, onConfirm }: RideTicketDialogProps): ReactElement | null {
  const { t } = useT();
  const { v } = useVocab();
  const [channel, setChannel] = useState<SupportDeskChannel>('PHONE');
  const [category, setCategory] = useState<SupportTicketCategory>(defaultCategory);

  useEffect(() => {
    if (!open) return;
    setChannel('PHONE');
    setCategory(defaultCategory);
  }, [open, defaultCategory]);

  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!busy) onConfirm({ channel, category });
  };

  return (
    <FormDialog open={open} id="ride-ticket" icon="pencil" title={t('rideTicket.title', { code: rideCode })} body={t('rideTicket.body')} busy={busy} initialFocusId={CHANNEL_ID} onCancel={onCancel}>
      <FormStack gap={4} onSubmit={submit} noValidate>
        <Field label={t('desk.new.channel')} htmlFor={CHANNEL_ID}>
          <Select id={CHANNEL_ID} value={channel} onChange={(e) => setChannel(e.target.value as SupportDeskChannel)} disabled={busy}>
            {SUPPORT_DESK_CHANNELS.map((c) => <option key={c} value={c}>{v('support.support_channel', c)}</option>)}
          </Select>
        </Field>
        <Field label={t('desk.new.category')} htmlFor={CATEGORY_ID}>
          <Select id={CATEGORY_ID} value={category} onChange={(e) => setCategory(e.target.value as SupportTicketCategory)} disabled={busy}>
            {SUPPORT_TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{v('support.support_category', c)}</option>)}
          </Select>
        </Field>
        {error !== undefined && (
          <div>
            <Notice tone="danger">{error}</Notice>
          </div>
        )}
        <Row gap={2} align="stretch">
          <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={onCancel} disabled={busy}>{t('ui.cancel')}</Button>
          <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" disabled={busy}>{busy ? t('rideTicket.sending') : t('rideTicket.confirm')}</Button>
        </Row>
      </FormStack>
    </FormDialog>
  );
}
